import { useId, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';

interface ViewerSessionZoneLabelProps {
  shortZone: string;
  timeZone: string;
}

const VIEWPORT_MARGIN = 8;

const ViewerSessionZoneLabel = ({
  shortZone,
  timeZone,
}: ViewerSessionZoneLabelProps) => {
  const tooltipId = useId();
  const buttonRef = useRef<HTMLButtonElement>(null);
  const tooltipRef = useRef<HTMLSpanElement>(null);
  const [hovered, setHovered] = useState(false);
  const [focused, setFocused] = useState(false);
  const visible = hovered || focused;

  useLayoutEffect(() => {
    if (!visible) return undefined;
    const position = () => {
      const button = buttonRef.current?.getBoundingClientRect();
      const tooltip = tooltipRef.current;
      if (!button || !tooltip) return;
      const { width, height } = tooltip.getBoundingClientRect();
      tooltip.style.left = `${Math.max(VIEWPORT_MARGIN, Math.min(button.left, window.innerWidth - width - VIEWPORT_MARGIN))}px`;
      tooltip.style.top = `${button.top - height >= VIEWPORT_MARGIN ? button.top - height : button.bottom}px`;
    };
    position();
    window.addEventListener('resize', position);
    window.addEventListener('scroll', position, true);
    return () => {
      window.removeEventListener('resize', position);
      window.removeEventListener('scroll', position, true);
    };
  }, [visible, timeZone]);

  return (
    <span
      className="inline-block whitespace-nowrap"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
    >
      <button
        ref={buttonRef}
        aria-describedby={visible ? tooltipId : undefined}
        aria-label={`Timezone: ${timeZone}`}
        className="cursor-help rounded-sm border-0 bg-transparent p-0 text-inherit focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-sage-500"
        style={{ font: 'inherit' }}
        type="button"
        onBlur={() => setFocused(false)}
        onFocus={() => setFocused(true)}
      >
        {shortZone}
      </button>
      {visible &&
        createPortal(
          <span
            ref={tooltipRef}
            className="pointer-events-none fixed z-50 max-w-64 whitespace-normal break-words rounded bg-cement-500 px-2 py-1 text-xs font-normal text-white shadow-md"
            id={tooltipId}
            role="tooltip"
            style={{
              backgroundColor: '#262626',
              borderRadius: 4,
              color: '#fff',
              maxWidth: `calc(100vw - ${VIEWPORT_MARGIN * 2}px)`,
              padding: '4px 8px',
              position: 'fixed',
              zIndex: 1000,
            }}
          >
            {timeZone}
          </span>,
          document.body,
        )}
    </span>
  );
};

export default ViewerSessionZoneLabel;
